import { Component, Injectable, Input, NgZone, ViewChild } from '@angular/core'
import { FormControl } from '@angular/forms'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { TemplateType } from '@tokeet-frontend/templates'
import { AttachmentDialogService } from '@tv3/shared/attachment/attachment-dialog.service'
import { Attachment, defaultTinyMCEOptions, Destroyable, getAttachmentLinks } from '@tokeet-frontend/tv3-platform'
import { TinyMceEditorOptions, TinyMceEditor, TinyMceEditorComponent } from '@tokeet-frontend/tv3-platform'
import { DataDictionaryDialogService } from '@tv3/shared/data-dictionary-dialog/data-dictionary-dialog.service'
import { SharedModule } from '@tv3/shared/shared.module'
import { EditorImageDialogPatchService } from '@tv3/shared/dialogs/editor-image/editor-image-dialog.patch.service'

@Injectable({ providedIn: SharedModule })
export class EditorRichComponentDependencies {
  constructor(
    public store: Store<any>,
    public ngZone: NgZone,
    public attachmentDialog: AttachmentDialogService,
    public editorImageDialogPatch: EditorImageDialogPatchService,
    public dataDictionaryDialog: DataDictionaryDialogService
  ) {}
}

@Component({
  template: '',
})
export abstract class EditorRichComponent extends Destroyable {
  @ViewChild('tinyMceEditor') tinyMceEditor: TinyMceEditorComponent
  @Input() type = TemplateType.Payload
  @Input() contentCtrl: FormControl
  @Input() subjectCtrl: FormControl
  @Input() attachmentsCtrl: FormControl

  @Input() dictToken$: Observable<string>

  editorOptions: TinyMceEditorOptions = {
    toolbar: `${defaultTinyMCEOptions.toolbar} | attachment image | datadic`,
    automatic_uploads: true,
  }

  isPlain = true
  types = TemplateType

  protected store: Store<any>
  protected ngZone: NgZone
  protected attachmentDialog: AttachmentDialogService
  protected editorImageDialogPatch: EditorImageDialogPatchService
  protected dataDictionaryDialog: DataDictionaryDialogService

  constructor(dependencies: EditorRichComponentDependencies) {
    super()

    this.store = dependencies.store
    this.ngZone = dependencies.ngZone
    this.attachmentDialog = dependencies.attachmentDialog
    this.editorImageDialogPatch = dependencies.editorImageDialogPatch
    this.dataDictionaryDialog = dependencies.dataDictionaryDialog
  }

  onEditorSetup(editor: TinyMceEditor) {
    editor.on('init', () => {
      this.editorImageDialogPatch.patchEditorWindowOpen(editor)
    })

    editor.ui.registry.addButton('datadic', {
      icon: 'book',
      tooltip: $localize`You may insert data tokens using data dictionary.`,
      onAction: () => this.ngZone.run(() => this.handleDataDict(editor)),
    })

    editor.ui.registry.addButton('attachment', {
      icon: 'paperclip',
      tooltip: $localize`Attachments`,
      onAction: () => this.ngZone.run(() => this.handleAttachment(editor)),
    })
  }

  handleDataDict(editor: TinyMceEditor) {
    if (this.dictToken$) {
      this.dictToken$.subscribe((tag) => editor.insertContent(tag))
    } else {
      this.dataDictionaryDialog.open().subscribe((tag) => editor.insertContent(tag))
    }
  }

  handleAttachment(editor: TinyMceEditor) {
    this.attachmentDialog.open().subscribe((attachments: Attachment[]) => {
      const items = this.attachmentsCtrl?.value || []
      this.attachmentsCtrl?.setValue([...items, ...attachments])

      const message = this.contentCtrl.value || ''
      const content = getAttachmentLinks(attachments)
      this.contentCtrl.setValue(`${message} ${content}`)
    })
  }

  onDataDictionary() {
    if (!this.isPlain) {
      this.dataDictionaryDialog.open().subscribe((tag) => this.tinyMceEditor.editor.insertContent(tag))
    } else {
      this.dataDictionaryDialog.open().subscribe((tag) => {
        this.contentCtrl.setValue(this.contentCtrl.value + tag)
      })
    }
  }

  onToggleInputType() {
    this.isPlain = !this.isPlain
  }
}
